import {Component, OnInit, ViewChild} from '@angular/core';
import {PropertyDetailComponent} from "../property-detail/property-detail.component";
import {RentalPropertyStatus} from "../../../domain/enums/rental-property-status.enum";
import {buildValidationErrorMessage} from "../../../utils/invalid-fields.util";
import {MatButtonModule} from "@angular/material/button";
import {MatIconModule} from "@angular/material/icon";
import {MatProgressSpinnerModule} from "@angular/material/progress-spinner";
import {MatDividerModule} from "@angular/material/divider";
import {MatCardModule} from "@angular/material/card";
import {MatTabLink, MatTabNav, MatTabNavPanel, MatTabsModule} from "@angular/material/tabs";
import {ActivatedRoute, Router, RouterModule} from "@angular/router";
import {RentalPropertyComponent} from "../rental-property/rental-property.component";
import {ReportingComponent} from "../reporting/reporting.component";
import {
  EnrollmentPropertyLicensesComponent
} from "../../../common/enrollment-property-licenses/enrollment-property-licenses.component";
import {
  EnrollmentPropertyReportingComponent
} from "../../../common/enrollment-property-reporting/enrollment-property-reporting.component";
import {
  PropertyDetailManagedComponent
} from "../../../common/property-detail-managed/property-detail-managed.component";
import {PropertyDetailOwnedComponent} from "../../../common/property-detail-owned/property-detail-owned.component";
import {CustomerResumeBarComponent} from "../../../common/customer-resume-bar/customer-resume-bar.component";
import {PropertyService} from "../../../domain/services/property/property.service";
import {DialogService} from "../../../domain/services/dialog/dialog.service";
import {AccountService} from "../../../domain/services/account/account.service";
import {extractLastUrl} from "../../../utils/last-url-extractor";
import {EnrollmentType, EnrollmentTypeStrMap} from "../../../domain/enums/enrollment-type.enum";
import {AccountModel} from "../../../domain/models/account.model";
import {InactivateDialogComponent} from "../inactivate-dialog/inactivate-dialog.component";
import {PropertyInactivation} from "../../../domain/models/property-inactivation.model";
import {ReactivateDialogComponent} from "../reactivate-dialog/reactivate-dialog.component";
import {RentalPropertyModel} from "../../../domain/models/rental-property.model";
import {GlobalVariablesService} from "../../../domain/services/admin/global-variables.service";

@Component({
  selector: 'app-property-detail-page',
  standalone: true,
  imports: [PropertyDetailComponent, MatButtonModule, MatDividerModule, MatCardModule, MatIconModule, MatProgressSpinnerModule, MatTabLink, MatTabNav, RouterModule, MatTabNavPanel, MatTabsModule, RentalPropertyComponent, ReportingComponent, EnrollmentPropertyLicensesComponent, EnrollmentPropertyReportingComponent, PropertyDetailManagedComponent, PropertyDetailOwnedComponent, CustomerResumeBarComponent],
  templateUrl: './property-detail-page.component.html',
  styleUrl: './property-detail-page.component.css'
})
export class PropertyDetailPageComponent implements OnInit {
  @ViewChild(PropertyDetailComponent) propertyDetail?: PropertyDetailComponent;
  currentAccount: AccountModel | undefined;
  currentAccountType: EnrollmentType | undefined;
  isLoading = false;
  protected readonly RentalPropertyStatus = RentalPropertyStatus;

  constructor(private readonly route: ActivatedRoute, private readonly service: PropertyService, private readonly router: Router, private readonly dialogService: DialogService, private readonly accountService: AccountService, private readonly globalVariables: GlobalVariablesService) {
  }

  /**
   * Same certificate the portal prints (TDT-Web certificate-of-registration), for counter staff.
   * Branding (names, logo, signature) comes from Global Variables. Opens a print window.
   */
  printCertificate(): void {
    const acc = this.currentAccount;
    const p = acc?.rentalProperty;
    if (!acc || !p) return;
    const win = window.open('', '_blank');
    if (!win) return;
    this.globalVariables.getAll().subscribe(v => {
      const esc = (x: any) => String(x ?? '').replace(/[&<>"']/g, c => ({'&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;'}[c]!));
      const img = (x?: string) => (x ?? '').startsWith('data:image/') ? x! : '';
      const county = v['cert_county_name'] || v['county_name'] || 'County';
      const countyName = /county/i.test(county) ? county : `${county} County`;
      const collector = v['cert_tax_collector_name'] || v['tax_collector_name'] || 'County Tax Collector';
      const issuedTo = acc.entityName || [acc.firstName, acc.lastName].filter(Boolean).join(' ') || acc.companyName || '';
      const logo = img(v['platform_logo']);
      const signature = img(v['signature_image']);
      const contact = [v['tax_collector_phone'], v['street_address'], v['website_url']].filter(Boolean).map(esc).join('  |  ');
      const year = new Date().getFullYear();
      const issued = new Date().toLocaleDateString('en-US', {month: 'long', day: 'numeric', year: 'numeric'});
      win.document.write(`<!doctype html><html><head><title>Certificate of Registration</title><style>
        body{font-family:Inter,Arial,sans-serif;font-size:12px;color:#000;max-width:640px;margin:40px auto;}
        .head{text-align:center;margin-bottom:24px}.head img{max-height:56px;max-width:220px}
        .head .name{font-size:16px;font-weight:600;text-transform:uppercase}
        .bar{background:#D9D9D9;text-align:center;padding:6px;font-weight:500;text-transform:uppercase;margin-bottom:40px}
        p{line-height:1.8;margin:0 0 24px}.sig{margin-top:40px;width:180px}.sig img{max-height:32px}
        .line{border-top:1px solid #616161;margin:4px 0}.foot{text-align:center;color:#444746;font-size:10px;margin-top:80px}
      </style></head><body>
        <div class="head">${logo ? `<img src="${logo}" alt="">` : `<div class="name">${esc(collector)}</div>`}</div>
        <div class="bar">Certificate of Registration</div>
        <p><strong>Issued to: ${esc(issuedTo)}</strong></p>
        <p>For the rental property located at: ${esc(p.rentalPropertyStreetAddress)}, ${esc(p.city)}, FL, ${esc(p.zipCode)}</p>
        <p>This document certifies the registration of the above owner to collect, report and remit the ${esc(countyName)} Tourist Development Tax for the above property to the <strong>${esc(collector)}</strong>.</p>
        <p><strong>Expiration Date:</strong> December 31 ${year} or at such time as notification is sent to the ${esc(collector)} indicating no longer renting unit transiently.</p>
        <div class="sig">${signature ? `<img src="${signature}" alt="">` : '<div style="height:32px"></div>'}<div class="line"></div>
          <div>${esc(collector)}</div><div><em>Issued ${esc(issued)}</em></div></div>
        <div class="foot">${contact}</div>
        <script>window.onload = () => window.print();<\/script>
      </body></html>`);
      win.document.close();
    });
  }

  ngOnInit(): void {
    const id: number = Number(this.route.snapshot.params['id']);
    if (id) {
      this.accountService.getByPropertyId(id).subscribe(acc => {
        this.currentAccount = acc;
        this.currentAccountType = acc?.type ? EnrollmentTypeStrMap.get(acc.type) : undefined;
      })
    }
  }

  save() {
    const propertyComponent = this.propertyDetail?.getPropertyComponent();
    if (propertyComponent && propertyComponent.isFormValid()) {
      const updated: AccountModel = {...this.currentAccount, ...propertyComponent.getUpdatedAccount()}
      this.pushChanges(updated);
    } else {
      propertyComponent?.raiseFormValidations();
      setTimeout(() => {
        const msg = buildValidationErrorMessage();
        this.dialogService.openErrorDialog("Unable to submit", msg);
      });
    }
  }

  inactivateProperty() {
    this.dialogService.openCustomDialog(InactivateDialogComponent, (inactivation: PropertyInactivation) => {
      if (inactivation) {
        const propertyId = this.currentAccount?.rentalProperty?.id;
        if (propertyId) {
          inactivation.propertyId = propertyId;
          this.service.inactivateProperty(inactivation).subscribe(value => {
            this.dialogService
              .openSuccesDialog("Success", "Property information updated successfully.")
              .subscribe(() => {
                this.exit();
              });
          });
        } else {
          this.dialogService
            .openErrorDialog("Unable to submit", "Please refresh the page and try again.");
        }
      }
    });
  }

  reactivatePropery() {
    this.dialogService.openCustomDialog(ReactivateDialogComponent, (modifiedProperty: RentalPropertyModel) => {
      if (modifiedProperty) {
        let rentalProperty = {...this.currentAccount?.rentalProperty, ...modifiedProperty};
        this.service.updateProperty(rentalProperty).subscribe(() => {
          this.dialogService
            .openSuccesDialog("Success", "Property information updated successfully.")
            .subscribe(() => {
              this.exit();
            });
        })
      } else {
        this.dialogService
          .openErrorDialog("Unable to submit", "Please refresh the page and try again.");
      }
    });
  }

  onDiscardAndExit() {
    this.exit();
  }

  exit() {
    const lastUrl = this.router.lastSuccessfulNavigation;
    if (lastUrl !== undefined) {
      this.router.navigate([extractLastUrl(lastUrl)]);
    } else {
      this.router.navigate(["app/search/property"]);
    }
  }

  private pushChanges(updated: AccountModel) {
    this.isLoading = true;
    this.accountService.update(updated).subscribe(() => {
      // Stay on the current tab and reload the saved data instead of navigating
      // back to the list, then confirm with a success message.
      this.reload();
      this.dialogService.openSuccesDialog("Success", "Property information updated successfully.");
    }, error => {
      this.isLoading = false;
      this.dialogService
        .openErrorDialog("Error", "An error occurred while trying to update the property information.");
    });
  }

  /** Re-fetch the account so the tabs reflect the just-saved values without leaving the page. */
  private reload() {
    const id: number = Number(this.route.snapshot.params['id']);
    if (!id) { this.isLoading = false; return; }
    this.accountService.getByPropertyId(id).subscribe(acc => {
      this.currentAccount = acc;
      this.currentAccountType = acc?.type ? EnrollmentTypeStrMap.get(acc.type) : undefined;
      this.isLoading = false;
    }, () => { this.isLoading = false; });
  }
}
